import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Toaster } from "sonner";
import { api } from "../lib/api";
import { clearToken } from "../lib/auth";
import SingleShot from "../components/SingleShot";
import ReelComposer from "../components/ReelComposer";
import Library from "../components/Library";
import VideoPlayerModal from "../components/VideoPlayerModal";
import CrmSidebar from "../components/CrmSidebar";
import ClipTimeline from "../components/ClipTimeline";
import EnvHealthPill from "../components/EnvHealthPill";

export default function Workspace() {
  const [tab, setTab] = useState("single");
  const [libraryInitialTab, setLibraryInitialTab] = useState("clips");
  const [templates, setTemplates] = useState([]);
  const [reelPrefill, setReelPrefill] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [reels, setReels] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [activeListing, setActiveListing] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const nav = useNavigate();

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch {}
  }, []);

  const refreshJobs = useCallback(async () => {
    if (!activeListing?.id) {
      setJobs([]);
      return;
    }
    try {
      const { data } = await api.get(
        `/jobs?listing_id=${activeListing.id}`
      );
      setJobs(data);
      data
        .filter((j) => j.status === "in_progress" || j.status === "creating")
        .forEach((j) => api.get(`/jobs/${j.id}`).catch(() => {}));
    } catch {}
  }, [activeListing?.id]);

  const refreshReels = useCallback(async () => {
    if (!activeListing?.id) {
      setReels([]);
      return;
    }
    try {
      const { data } = await api.get(
        `/reels?listing_id=${activeListing.id}`
      );
      setReels(data);
      data
        .filter(
          (r) =>
            r.status === "in_progress" ||
            r.status === "stitching" ||
            r.status === "creating"
        )
        .forEach((r) => api.get(`/reels/${r.id}`).catch(() => {}));
    } catch {}
  }, [activeListing?.id]);

  // Poll any in-flight Day→Night reveals so they advance + stitch themselves
  const refreshReveals = useCallback(async () => {
    if (!activeListing?.id) return;
    try {
      const { data } = await api.get(
        `/reveals?listing_id=${activeListing.id}`
      );
      data
        .filter((r) => r.status !== "done" && r.status !== "failed")
        .forEach((r) => api.get(`/reveals/${r.id}`).catch(() => {}));
    } catch {}
  }, [activeListing?.id]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    refreshJobs();
    refreshReels();
    refreshReveals();
  }, [refreshJobs, refreshReels, refreshReveals]);

  // Background poll — refresh everything every 20s while a listing is open.
  // This ensures Day→Night reveals, Magnific jobs, and reel stitching all
  // surface in the UI without the user having to manually refresh.
  useEffect(() => {
    if (!activeListing?.id) return;
    const tick = () => {
      refreshJobs();
      refreshReels();
      refreshReveals();
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [activeListing?.id, refreshJobs, refreshReels, refreshReveals]);

  const handleSelect = (listingId, listing, realtor, company) => {
    if (!listingId) {
      setActiveListing(null);
      return;
    }
    setActiveListing({
      id: listingId,
      address: listing.address,
      realtor: realtor.name,
      company: company.name,
    });
  };

  const logout = () => {
    clearToken();
    nav("/login");
  };

  const tabs = [
    { id: "single", label: "Single Shot", icon: "Wand2" },
    { id: "reel", label: "Reel Composer", icon: "Layers" },
    { id: "compile", label: "Compile", icon: "Scissors" },
    { id: "library", label: "Library", icon: "FolderOpen" },
  ];

  const TabIcon = ({ name, size }) => {
    const C = Icons[name] || Icons.Square;
    return <C size={size} />;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
          },
        }}
      />

      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="px-4 md:px-8 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 md:gap-6 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden text-zinc-400 hover:text-white p-1"
              data-testid="mobile-menu-btn"
              aria-label="Open CRM menu"
            >
              <Icons.Menu size={20} />
            </button>
            <div className="font-display text-xl font-medium">
              Property Motion <span className="text-zinc-600">AI</span>
            </div>
            {activeListing && (
              <div className="hidden md:flex items-center gap-2 text-xs">
                <span className="text-zinc-600">{activeListing.company}</span>
                <Icons.ChevronRight size={10} className="text-zinc-700" />
                <span className="text-zinc-500">{activeListing.realtor}</span>
                <Icons.ChevronRight size={10} className="text-zinc-700" />
                <button
                  onClick={() => nav(`/listings/${activeListing.id}`)}
                  className="group inline-flex items-center gap-1.5 text-white hover:text-amber-300 transition-colors"
                  title="Open listing details — price, beds, baths, open house…"
                  data-testid="open-listing-details"
                >
                  <span>{activeListing.address}</span>
                  <Icons.SlidersHorizontal
                    size={11}
                    className="text-zinc-600 group-hover:text-amber-300 transition-colors"
                  />
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <div className="hidden md:flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-1">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  disabled={!activeListing}
                  className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 transition-all ${
                    tab === t.id
                      ? "bg-white text-black"
                      : "text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:text-zinc-400"
                  }`}
                  data-testid={`nav-tab-${t.id}`}
                >
                  <TabIcon name={t.icon} size={12} />
                  {t.label}
                </button>
              ))}
            </div>
            <button
              onClick={logout}
              className="text-xs text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 rounded-lg px-2.5 md:px-3 py-1.5 flex items-center gap-1.5"
              data-testid="logout-button"
            >
              <Icons.LogOut size={12} />
              <span className="hidden md:inline">Sign out</span>
            </button>
            <EnvHealthPill />
          </div>
        </div>
        {/* Mobile breadcrumb */}
        {activeListing && (
          <div className="md:hidden px-4 pb-2 text-[11px] text-zinc-400 truncate">
            {activeListing.address}
          </div>
        )}
      </header>

      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-sidebar-backdrop"
        />
      )}

      <div className="flex">
        <div
          className={`${
            sidebarOpen
              ? "fixed inset-y-0 left-0 z-50 translate-x-0"
              : "fixed inset-y-0 left-0 z-50 -translate-x-full md:static md:translate-x-0"
          } transition-transform duration-200 md:transition-none`}
        >
          <CrmSidebar
            activeListingId={activeListing?.id}
            onSelect={(lid, l, r, c) => {
              handleSelect(lid, l, r, c);
              setSidebarOpen(false);
            }}
          />
        </div>

        <main className="flex-1 px-4 md:px-8 py-4 md:py-8 max-w-[1600px] pb-24 md:pb-8 min-w-0">
          {!activeListing ? (
            <EmptyState />
          ) : (
            <>
              {tab === "single" && (
                <SingleShot
                  templates={templates}
                  listingId={activeListing.id}
                  onJobCreated={() => {
                    refreshJobs();
                    setLibraryInitialTab("clips");
                    setTab("library");
                  }}
                />
              )}
              {tab === "reel" && (
                <ReelComposer
                  templates={templates}
                  listingId={activeListing.id}
                  defaultTitle={activeListing.address}
                  prefill={reelPrefill}
                  onPrefillConsumed={() => setReelPrefill(null)}
                  onReelCreated={() => {
                    refreshReels();
                    setLibraryInitialTab("reels");
                    setTab("library");
                  }}
                />
              )}
              {tab === "compile" && (
                <ClipTimeline
                  listingId={activeListing.id}
                  defaultTitle={activeListing.address}
                  onCompiled={() => {
                    refreshReels();
                    setLibraryInitialTab("reels");
                    setTab("library");
                  }}
                />
              )}
              {tab === "library" && (
                <Library
                  jobs={jobs}
                  reels={reels}
                  refreshJobs={refreshJobs}
                  refreshReels={refreshReels}
                  onPlay={setPlaying}
                  initialTab={libraryInitialTab}
                  listingId={activeListing?.id}
                  onCloneReel={(reel) => {
                    setReelPrefill(reel);
                    setTab("reel");
                  }}
                />
              )}
            </>
          )}
        </main>
      </div>

      <VideoPlayerModal video={playing} onClose={() => setPlaying(null)} />

      {/* Mobile bottom nav — replaces the desktop tab bar on small screens */}
      {activeListing && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10">
          <div className="grid grid-cols-4">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 transition-all ${
                  tab === t.id ? "text-white" : "text-zinc-500"
                }`}
                data-testid={`mobile-nav-${t.id}`}
              >
                <TabIcon name={t.icon} size={18} />
                <span className="text-[10px]">{t.label}</span>
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center mt-8">
      <Icons.Building2 className="mx-auto text-zinc-700" size={36} />
      <div className="font-display text-2xl text-white mt-6 mb-2">
        Pick a listing to begin
      </div>
      <div className="text-sm text-zinc-500 max-w-md mx-auto">
        Use the sidebar to select a listing — or add a new company → realtor →
        listing. Every clip you generate is filed under that listing.
      </div>
    </div>
  );
}
